export type WsMessage =
  | { kind: 'binary'; data: Uint8Array }
  | { kind: 'text'; data: string };

export type WsCloseClassification =
  | 'unknown_session'   // 4404
  | 'room_busy'         // 4409
  | 'rate_limited'      // 4429
  | 'room_expired'      // 4408 — bridged-room idle timeout (relay/internal/room/room.go; SPEC §A.8). Also arrives as a pre-close text notify {"type":"room_expired"} on the awaiting leg.
  | 'reconnect_denied'  // 4407 — agent denied reconnect (window expired / forgotten)
  | 'disconnected'      // any other code post-handshake
  | 'ws_open_failed';   // network failure before open

const SUBPROTOCOL = 'heligraph.v1';

/** A half-open TCP connect (e.g. a phone that just switched Wi-Fi to LTE) can hang for minutes without any browser event. */
export const OPEN_TIMEOUT_MS = 10_000;

export type WsClose = { kind: 'close'; code: number; reason: string };
export type WsTimeout = { kind: 'timeout' };

export interface RelayWs {
  send(bytes: Uint8Array): void;
  /** Like the no-arg form, but resolves `{ kind: 'timeout' }` after `timeoutMs`; the message that arrives later stays queued. */
  next(timeoutMs: number): Promise<WsMessage | WsClose | WsTimeout>;
  // Declared last: TS contextually types a mock's `next()` from the last overload, so it must be the timeout-free one.
  /** Resolves to the next message or to a close; waits indefinitely. */
  next(): Promise<WsMessage | WsClose>;
  close(code?: number, reason?: string): void;
}

/**
 * Open a WS to wss://<relayHost>/r/{sid}. Resolves on `open`, rejects on early close/error,
 * or with `ws_open_failed_timeout` if the socket is still not open after `openTimeoutMs`.
 */
export function openRelayWs(
  relayUrl: string,
  sid: string,
  openTimeoutMs: number = OPEN_TIMEOUT_MS,
): Promise<RelayWs> {
  return new Promise((resolve, reject) => {
    const url = `${relayUrl.replace(/\/$/, '')}/r/${encodeURIComponent(sid)}`;
    const ws = new WebSocket(url, [SUBPROTOCOL]);
    ws.binaryType = 'arraybuffer';

    type Msg = WsMessage | WsClose;
    const queue: Array<Msg> = [];
    const waiters: Array<(m: Msg) => void> = [];

    // Browsers report readyState CLOSED (not CONNECTING) in the error/close events of a failed
    // connect, so "not opened yet" has to be tracked here instead of read from the socket.
    let opened = false;

    // Cleared on every settle path, so firing means the socket never opened.
    const openTimer = setTimeout(() => {
      ws.close();
      reject(new Error('ws_open_failed_timeout'));
    }, openTimeoutMs);

    const dispatch = (msg: Msg) => {
      const w = waiters.shift();
      if (w !== undefined) {
        w(msg);
      } else {
        queue.push(msg);
      }
    };

    function next(): Promise<Msg>;
    function next(timeoutMs: number): Promise<Msg | WsTimeout>;
    function next(timeoutMs?: number): Promise<Msg | WsTimeout> {
      const head = queue.shift();
      if (head !== undefined) return Promise.resolve(head);
      return new Promise((r) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const waiter = (m: Msg) => {
          if (timer !== undefined) clearTimeout(timer);
          r(m);
        };
        waiters.push(waiter);
        if (timeoutMs !== undefined) {
          // Drop our own waiter, otherwise it would swallow the next message.
          timer = setTimeout(() => {
            const i = waiters.indexOf(waiter);
            if (i !== -1) waiters.splice(i, 1);
            r({ kind: 'timeout' });
          }, timeoutMs);
        }
      });
    }

    ws.onopen = () => {
      opened = true;
      clearTimeout(openTimer);
      resolve({
        send(bytes) {
          ws.send(bytes);
        },
        next,
        close(code, reason) {
          ws.close(code, reason);
        },
      });
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        dispatch({ kind: 'text', data: ev.data });
      } else if (ev.data instanceof ArrayBuffer) {
        dispatch({ kind: 'binary', data: new Uint8Array(ev.data) });
      }
    };

    ws.onerror = () => {
      // The browser fires onerror without details on a failed connect; reject if not open yet.
      if (!opened) {
        clearTimeout(openTimer);
        reject(new Error('ws_open_failed'));
      }
    };

    ws.onclose = (ev) => {
      if (!opened) {
        clearTimeout(openTimer);
        reject(new Error(`ws_open_failed_${ev.code}_${ev.reason}`));
        return;
      }
      dispatch({ kind: 'close', code: ev.code, reason: ev.reason });
    };
  });
}

export function classifyCloseCode(code: number, reason: string): WsCloseClassification {
  switch (code) {
    case 4404:
      return 'unknown_session';
    case 4409:
      return 'room_busy';
    case 4429:
      return 'rate_limited';
    case 4408:
      return 'room_expired';
    case 4407:
      return 'reconnect_denied';
    default:
      if (reason === 'unknown_session') return 'unknown_session';
      if (reason === 'room_busy') return 'room_busy';
      if (reason === 'room_expired') return 'room_expired';
      return 'disconnected';
  }
}

/** Parse a text frame as RelayNotification. Returns null on parse failure. */
export function parseRelayNotification(text: string): { type: string; ts?: number } | null {
  try {
    const v = JSON.parse(text) as unknown;
    if (v !== null && typeof v === 'object' && 'type' in v && typeof (v as { type: unknown }).type === 'string') {
      return v as { type: string; ts?: number };
    }
    return null;
  } catch {
    return null;
  }
}
